/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import ReactSelect, {
  components,
  createFilter,
  ActionMeta,
  SingleValue as SingleValueType,
  MultiValue,
  StylesConfig,
  ClearIndicatorProps as ReactSelectClearIndicatorProps,
  ValueContainerProps as ReactSelectValueContainerProps,
  SingleValueProps as ReactSelectSingleValueProps,
  OptionProps as ReactSelectOptionProps,
  MenuListProps as ReactSelectMenuListProps,
  GroupBase
} from 'react-select'
import CreatableSelect from 'react-select/creatable'
import React, { Component, useEffect, useState } from 'react'
import { FixedSizeList as List } from 'react-window'
import styled from 'styled-components'
import { useFormikContext } from 'formik'

const optionHeightPx = 31

export interface OptionType {
  value: any
  label: string
  isFixed?: boolean
  isDisabled?: boolean
  hasBottomSeparator?: boolean
}

interface SelectProps {
  value?: any
  options?: OptionType[]
  creatable?: boolean
  style?: Partial<StylesConfig<OptionType, boolean>>
  multi?: boolean
  id?: string
  name?: string
  onChange?: (option: SingleValueType<OptionType> | MultiValue<OptionType>, actionMeta: ActionMeta<OptionType>) => void
  [key: string]: any
}

interface CreatableProps extends SelectProps {
  creatable?: true
}

interface CustomSelectStylesParams {
  override: Partial<StylesConfig<OptionType, boolean>>
}

const OptionValueSpan = styled.span`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal; /* Critical fix: 'initial' often defaults to 'nowrap' in some browsers */
  word-break: break-word;
  line-height: 1.3;
  width: 100%;
  font-size: 12px;
  color: #666;
  padding: 2px 0;
`

export function getCustomSelectStyles({ override }: CustomSelectStylesParams): StylesConfig<OptionType, boolean> {
  return {
    control: (provided) => ({
      ...provided,
      borderRadius: '0',
      boxShadow: 'none',
      border: '1px solid #ccc',
      minHeight: '33px',
      '&:hover': {
        borderColor: '#ccc'
      },
      ...override.control
    }),
    input: (provided) => ({
      ...provided,
      fontSize: '12px',
      ...override.input
    }),
    placeholder: (provided) => ({
      ...provided,
      color: '#666',
      fontSize: '12px'
    }),
    menu: (provided) => ({
      ...provided,
      marginTop: '0',
      zIndex: 9999
    }),
    clearIndicator: (provided) => ({
      ...provided,
      color: '#999',
      cursor: 'pointer',
      padding: '0px 5px',
      fontSize: '18px',
      alignSelf: 'center'
    }),
    dropdownIndicator: (provided, state) => ({
      ...provided,
      color: '#999',
      cursor: 'pointer',
      padding: '1px 5px 0 0',
      '> svg': {
        marginTop: state.selectProps.menuIsOpen ? '0' : '3px',
        borderColor: state.selectProps.menuIsOpen ? 'transparent transparent #999' : '#999 transparent transparent',
        borderStyle: 'solid',
        borderWidth: state.selectProps.menuIsOpen ? '0 5px 5px' : '5px 5px 2.5px',
        display: 'inline-block',
        height: 0,
        width: 0,
        position: 'relative'
      },
      alignSelf: 'baseline',
      marginTop: '10px'
    }),
    noOptionsMessage: (provided) => ({
      ...provided,
      fontSize: '12px',
      padding: '1px'
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '12px',
      color: '#666',
      height: optionHeightPx + 'px', // Match virtualization height
      lineHeight: '14px',
      padding: '0px 12px',
      backgroundColor: state.isSelected ? '#E9F0F7' : state.data && state.data.isDisabled ? '#F4F4F4' : '#fff',
      borderBottom: state.data && state.data.hasBottomSeparator ? '1px solid #C9C9C9' : 'none',
      display: 'flex',
      alignItems: 'center',
      '&:hover': {
        backgroundColor: '#E9F0F7'
      }
    }),
    indicatorSeparator: (provided) => ({
      ...provided,
      display: 'none'
    }),
    valueContainer: (provided) => {
      return {
        ...provided,
        ...override.valueContainer
      }
    },
    singleValue: (provided) => {
      return {
        ...provided,
        ...override.singleValue
      }
    },
    multiValue: (base, state) => {
      return state.data.isFixed || state.data.isDisabled ? { ...base, backgroundColor: '#C9C9C9' } : base
    },
    multiValueLabel: (provided, state: any) => ({
      ...provided,
      color: '#333',
      fontSize: '12px',
      padding: '1px',
      backgroundColor: '#E9F0F7',
      ...(state.data && (state.data.isFixed || state.data.isDisabled) ? { backgroundColor: '#C9C9C9' } : {})
    }),
    multiValueRemove: (base, state: any) => ({
      ...base,
      ...(state.isDisabled || (state.data && (state.data.isFixed || state.data.isDisabled)) ? { display: 'none' } : {}),
      color: '#333',
      backgroundColor: '#E9F0F7',
      '&:hover': {
        color: '#333',
        backgroundColor: '#E9F0F7'
      }
    })
  }
}

export const ClearIndicator: React.FC<ReactSelectClearIndicatorProps<OptionType, boolean, GroupBase<OptionType>>> = (
  props
) => {
  const {
    getStyles,
    innerProps: { ref, ...restInnerProps }
  } = props
  return (
    <div {...restInnerProps} ref={ref} style={getStyles('clearIndicator', props) as React.CSSProperties}>
      ×
    </div>
  )
}

export const ValueContainer: React.FC<ReactSelectValueContainerProps<OptionType, boolean, GroupBase<OptionType>>> = ({
  children,
  ...props
}) => {
  // additional props for Tosca identification
  const vals = props.getValue()
  const value = vals && vals.length > 0 ? vals.map((val: OptionType) => val.label).join(',') : ''
  return (
    <components.ValueContainer {...props}>
      <span id={`${props.selectProps.id}-value`} className={'d-none'} data-value={value}></span>
      {children}
    </components.ValueContainer>
  )
}

const SingleValue: React.FC<ReactSelectSingleValueProps<OptionType, boolean, GroupBase<OptionType>>> = ({
  children,
  ...props
}) => (
  <components.SingleValue {...props}>
    <span title={String(children)}>{children}</span>
  </components.SingleValue>
)

export const Option: React.FC<ReactSelectOptionProps<OptionType, boolean, GroupBase<OptionType>>> = (props) => {
  const { innerProps, ...restProps } = props
  const { onMouseMove, onMouseOver, ...restInnerProps } = innerProps

  return (
    <components.Option {...restProps} innerProps={restInnerProps}>
      <OptionValueSpan title={String(props.children)}>{props.children}</OptionValueSpan>
    </components.Option>
  )
}

export class MenuList extends Component<ReactSelectMenuListProps<OptionType, boolean, GroupBase<OptionType>>> {
  render() {
    const { options, children, maxHeight, getValue } = this.props
    const [value] = getValue()
    const itemsCount = Array.isArray(children) ? children.length : 0
    const itemHeight = optionHeightPx
    const notLargeList = maxHeight > itemsCount * itemHeight
    const initialOffset = notLargeList ? 0 : (options as OptionType[]).indexOf(value) * itemHeight
    const listHeight = notLargeList ? itemsCount * itemHeight : maxHeight
    return (
      <List
        height={listHeight}
        itemCount={Array.isArray(children) ? children.length : 0}
        itemSize={itemHeight}
        initialScrollOffset={initialOffset}
      >
        {({ index, style }: { index: number; style: React.CSSProperties }) => (
          <div style={style}>{Array.isArray(children) ? children[index] : null}</div>
        )}
      </List>
    )
  }
}

export const Select: React.FC<SelectProps> = ({ value, options, creatable, style, ...props }) => {
  const [parsedValue, setParsedValue] = useState<SingleValueType<OptionType> | MultiValue<OptionType> | null>(null)
  const formikContext = useFormikContext()

  useEffect(() => {
    if (value !== undefined && options !== undefined && Array.isArray(options)) {
      const val = Array.isArray(value)
        ? options.filter((x) => value.includes(x.value))
        : options.find((x) => x.value === value)
      setParsedValue(val === undefined ? null : val)
    }
  }, [value, options])

  const onChange = (
    option: SingleValueType<OptionType> | MultiValue<OptionType>,
    actionMeta: ActionMeta<OptionType>
  ) => {
    switch (actionMeta.action) {
      case 'remove-value':
      case 'pop-value':
        if (actionMeta.removedValue && (actionMeta.removedValue.isFixed || actionMeta.removedValue.isDisabled)) {
          return
        }
        break
      case 'clear':
        if (
          actionMeta &&
          actionMeta.removedValues &&
          Array.isArray(actionMeta.removedValues) &&
          actionMeta.removedValues.length &&
          (props.multi || props.isMulti)
        ) {
          option = actionMeta.removedValues.filter((v) => v.isFixed || v.isDisabled)
        }
        break
    }

    setParsedValue(option)
    props.onChange && props.onChange(option, actionMeta)
  }

  const attrs = {
    value: parsedValue,
    inputId: `${props.id}-input`,
    className:
      'select-input' +
      (formikContext?.errors && props.name && (formikContext.errors as any)?.[props.name] ? ' has-error' : ''),
    isClearable: true,
    isSearchable: true,
    components: { ClearIndicator, ValueContainer, MenuList, Option, SingleValue },
    filterOption: createFilter({ ignoreAccents: false }),
    menuPortalTarget: typeof window !== 'undefined' ? window.document.body : undefined,
    styles: {
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
      ...getCustomSelectStyles({ override: style ? style : {} }),
      zIndex: 9999
    },
    isMulti: !!props.multi,
    options,
    ...props,
    onChange: onChange
  }

  return creatable ? <CreatableSelect {...attrs} /> : <ReactSelect {...attrs} />
}

export const Creatable: React.FC<CreatableProps> = (props) => <Select creatable={true} {...props} />
